import React, { useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import OtpModal from './otpModal'; 
import AlertModal from '../reusable/AlertModal';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const [globalAlert, setGlobalAlert] = useState({ isOpen: false, title: "", message: "" });
  // NEW: Hard Lockout State
  const [isBlocked, setIsBlocked] = useState(false); 

  // OTP States
  const [showOTP, setShowOTP] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [otpError, setOtpError] = useState("");

  const [formData, setFormData] = useState({
    identifier: "", 
    password: "",   
    confirmPassword: ""
  });

  // --- LOCKOUT DETECTOR ---
  // Checks backend messages for lockout keywords
  const checkIfBlocked = (msg) => {
    const lowerMsg = msg.toLowerCase();
    if (lowerMsg.includes("limit") || lowerMsg.includes("block") || lowerMsg.includes("exceed") || lowerMsg.includes("wait") || lowerMsg.includes("hour")) {
      setGlobalAlert({
        isOpen: true,
        title: "Action Blocked",
        message: "Too many attempts. Please try again after some time to protect your account security."
      });
      return true;
    }
    return false;
  };

  const validateAll = () => {
    let newErrors = {};
    if (!/^[6-9]\d{9}$/.test(formData.identifier)) {
      newErrors.identifier = "Enter a valid 10-digit mobile number.";
    }
    if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters.";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  // --- STEP 1: REQUEST PASSWORD RESET ---
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (validateAll()) {
      setLoading(true);
      try {
        const payload = {
          identifier: formData.identifier,
          password: formData.password
        };

        const response = await axiosInstance.post('/auth/forgot', payload);

        if (response.status === 200 || response.status === 201) {
          setRequestId(response.data.request_id);
          setShowOTP(true); 
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.error || "Failed to initiate password reset.";
        setApiError(msg);
        
        // Check if the backend just locked us out (3 attempts)
        checkIfBlocked(msg);
      } finally {
        setLoading(false);
      }
    }
  };

  // --- STEP 2: VERIFY OTP TO FINALIZE RESET ---
  const handleVerifyOTP = async (otpValue) => {
    setOtpError("");
    try {
      const response = await axiosInstance.post('/auth/verify-reset', {
        request_id: requestId,
        otp: otpValue
      });

      if (response.status === 200 || response.status === 201) {
        alert("Password successfully reset! You can now log in.");
        setShowOTP(false);
        onClose(); 
        window.location.reload(); 
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || "Invalid OTP.";
      setOtpError(msg);
      
      // If 3 incorrect OTP attempts hit, the backend will send a block message
      if (checkIfBlocked(msg)) {
        setTimeout(() => {
          setShowOTP(false); // Close OTP Modal
          setApiError(msg);  // Show the block message on the main modal
        }, 2000);
        return; // Stop execution here
      }
      
      if (msg.toLowerCase().includes("expire") || msg.toLowerCase().includes("session")) {
        setTimeout(() => {
          setShowOTP(false);
          setApiError("Session expired. Please request a new password reset.");
        }, 2000);
      }
    }
  };

  // --- RESEND OTP LOGIC ---
  const handleResendOTP = async () => {
    setOtpError("");
    try {
      const response = await axiosInstance.post('/auth/forgot', {
        identifier: formData.identifier,
        password: formData.password
      });
      
      if (response.status === 200 || response.status === 201) {
        if (response.data.request_id) setRequestId(response.data.request_id);
        console.log("otp:", response.data.otp_code);
        setOtpError("✅ New OTP sent successfully!");
        setTimeout(() => setOtpError(""), 3000);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || "Failed to resend OTP.";
      setOtpError(msg);
      
      // If they hit the resend limit, lock the modal
      if (checkIfBlocked(msg)) {
        setTimeout(() => {
          setShowOTP(false);
          setApiError(msg);
        }, 2000);
      }
    }
  };

  const maskPhoneNumber = (phone) => {
    if (!phone || phone.length < 4) return phone;
    return `+91 ${"*".repeat(phone.length - 4)}${phone.slice(-4)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      
      <div className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold flex items-center justify-center transition-colors">
          ×
        </button>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold text-gray-900">Reset Password</h2>
          <p className="text-gray-500 mt-2 text-sm">Enter your registered mobile number and your new password.</p>
        </div>

        {/* Dynamic Error Box: Turns red and bold if blocked */}
        {apiError && (
          <div className={`mb-4 p-4 border rounded-xl text-sm font-semibold text-center flex flex-col items-center gap-1 ${isBlocked ? 'bg-red-100 border-red-300 text-red-800' : 'bg-red-50 border-red-100 text-red-600 animate-pulse'}`}>
            {isBlocked && (
              <svg className="w-6 h-6 text-red-600 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
            )}
            {apiError}
          </div>
        )}

        {/* We disable the entire form visually if blocked */}
        <form onSubmit={handleForgotSubmit} className={`space-y-5 transition-opacity duration-300 ${isBlocked ? 'opacity-50 pointer-events-none' : ''}`}>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Mobile Number</label>
            <input
              type="text"
              name="identifier"
              maxLength="10"
              disabled={isBlocked || loading}
              value={formData.identifier}
              onChange={(e) => handleChange({ target: { name: 'identifier', value: e.target.value.replace(/\D/g, '') }})}
              className={`w-full px-4 py-3 border ${errors.identifier ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all disabled:bg-gray-100`}
              placeholder="Enter 10-digit number"
            />
            {errors.identifier && <p className="text-red-500 text-xs mt-1 font-medium">{errors.identifier}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              disabled={isBlocked || loading}
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-4 py-3 border ${errors.password ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all disabled:bg-gray-100`}
              placeholder="••••••••"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} disabled={isBlocked} className="absolute right-4 top-9 text-xs font-bold text-gray-400 hover:text-indigo-600 disabled:opacity-50">
              {showPassword ? "HIDE" : "SHOW"}
            </button>
            {errors.password && <p className="text-red-500 text-xs mt-1 font-medium">{errors.password}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              disabled={isBlocked || loading}
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full px-4 py-3 border ${errors.confirmPassword ? 'border-red-400' : 'border-gray-300'} rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all disabled:bg-gray-100`}
              placeholder="••••••••"
            />
            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} disabled={isBlocked} className="absolute right-4 top-9 text-xs font-bold text-gray-400 hover:text-indigo-600 disabled:opacity-50">
              {showConfirmPassword ? "HIDE" : "SHOW"}
            </button>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1 font-medium">{errors.confirmPassword}</p>}
          </div>

          <button
            type="submit"
            disabled={isBlocked || loading}
            className={`w-full py-3.5 mt-2 rounded-xl font-bold text-white shadow-lg transition-all
              ${isBlocked ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400'}`}
          >
            {isBlocked ? "Access Locked" : (loading ? "Processing..." : "Create New Password")}
          </button>
        </form>
      </div>

      <OtpModal 
        isOpen={showOTP}
        onClose={() => {
          setShowOTP(false);
          setOtpError("");
        }}
        phoneNumber={maskPhoneNumber(formData.identifier)}
        apiError={otpError}
        onVerify={handleVerifyOTP}
        onResend={handleResendOTP}
      />

    <AlertModal 
          isOpen={globalAlert.isOpen}
          title={globalAlert.title}
          message={globalAlert.message}
          onClose={() => setGlobalAlert({ ...globalAlert, isOpen: false })}
          />
 
    </div>

    
  );
};

export default ForgotPasswordModal;